import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  createVariantAttribute,
  getAllVariantAttributes,
  updateVariantAttribute,
  deleteVariantAttribute,
} from "../../api/productVariantApi"; // Adjust path
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import Table from "../../core/pagination/datatable";
import CommonFooter from "../../core/common/footer/commonFooter";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import { getCurrentUserPerms, hasWrite, isCurrentUserAdmin } from "../../utils/perm";
// import ReactTagsInput from "../settings/reacttaginputs";

const VariantAttributes = () => {
  const user = useSelector((state) => state?.auth?.user);
  const userPerms = getCurrentUserPerms(user?.permissions || []);
  const canWriteVariants =
    isCurrentUserAdmin(user) ||
    userPerms.includes("*") ||
    hasWrite(userPerms, "variantAttributes");

  const [variantName, setVariantName] = useState({ en: "", ar: "" });
  // const [variantValues, setVariantValues] = useState([{ en: "", ar: "" }]);
  const [status, setStatus] = useState(true);
  const [dataSource, setDataSource] = useState([]);

  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState({ en: "", ar: "" });
  const [editValues, setEditValues] = useState([{ en: "", ar: "" }]);
  const [editStatus, setEditStatus] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [variantValues, setVariantValues] = useState([
    { en: "", ar: "", colorCode: "" },
  ]);

  const handleAddVariant = async () => {
    if (!canWriteVariants) {
      toast.error(`❌ ${window.translate("You don't have permission to modify variant attributes")}`);
      return;
    }
    if (!variantName.en.trim() || !variantName.ar.trim()) {
      toast.error(`${window.translate("Both English and Arabic variant names are required")}`);
      return;
    }
    if (variantValues.length === 0) {
      toast.error(`${window.translate("Please enter at least one value")}`);
      return;
    }

    const payload = {
      name: variantName,
      values: variantValues,
      status: status ? "on" : "off",
    };

    try {
      await createVariantAttribute(payload);
      // toast.success("Variant attribute created successfully");
      toast.success(` ${window.translate("Variant attribute created successfully")}`);
      // Reset form
      setVariantName("");
      setVariantValues([]);
      setStatus(true);
      document.getElementById("close-add-variant-btn")?.click();
      // Delay then reload
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (error) {
      toast.error(`${window.translate("Failed to create variant attribute")}`);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getAllVariantAttributes();
        setDataSource(data);
      } catch (err) {
        
        toast.error(`${window.translate("Failed to load variant attributes")}`);
        console.error(err);
      }
    };

    fetchData();
  }, []);

  // Function to handle opening the edit modal with data
  const handleEditOpen = (record) => {
    setEditId(record.id || record._id);
    setEditName(record.name || { en: "", ar: "" });

    // const safeValues = Array.isArray(record.values)
    //   ? record.values.map((val) =>
    //       typeof val === "object" && val !== null
    //         ? { en: val.en || "", ar: val.ar || "" }
    //         : { en: val || "", ar: "" }
    //     )
    //   : [{ en: "", ar: "" }];
    const safeValues = Array.isArray(record.values)
  ? record.values.map((val) =>
      typeof val === "object" && val !== null
        ? {
            en: val.en || "",
            ar: val.ar || "",
            colorCode: val.colorCode || "#000000", // <-- capture colorCode here
          }
        : { en: val || "", ar: "", colorCode: "#000000" }
    )
  : [{ en: "", ar: "", colorCode: "#000000" }];

    setEditValues(safeValues);
    setEditStatus(record.rawStatus === "on");

    setTimeout(() => {
      document.getElementById("open-edit-variant-btn")?.click();
    }, 100);
  };

  // Function to handle update
  const handleUpdateVariant = async () => {
    if (!canWriteVariants) {
      toast.error(`❌ ${window.translate("You don't have permission to modify variant attributes")}`);
      return;
    }
    if (!editName.en.trim() || !editName.ar.trim()) {
      
      toast.error(`${window.translate("Both English and Arabic variant names are required")}`);
      return;
    }
    if (
      !editValues.length ||
      editValues.some((val) => !val.en.trim() || !val.ar.trim())
    ) {
      toast.error("All values must be filled in both languages");
      return;
    }

    const payload = {
      name: editName,
      values: editValues,
      status: editStatus ? "on" : "off",
    };

    try {
      await updateVariantAttribute(editId, payload);
      // toast.success("Variant attribute updated successfully");
      toast.success(` ${window.translate("Variant attribute updated successfully")}`);
      setEditId(null);
      setEditName({ en: "", ar: "" });
      setEditValues([{ en: "", ar: "" }]);
      setEditStatus(true);
      document.getElementById("close-edit-variant-btn")?.click();

      const data = await getAllVariantAttributes();
      setDataSource(data);
      // Delay then reload
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (err) {
      toast.error(`${window.translate("Failed to update variant attribute")}`);
    }
  };

  // delete api
  const handleDeleteVariant = async () => {
    if (!canWriteVariants) {
      toast.error(`❌ ${window.translate("You don't have permission to modify variant attributes")}`);
      return;
    }
    if (!deleteId) {
      
      toast.error(`❌ ${window.translate("Variant ID not found")}`);
      return;
    }

    try {
      await deleteVariantAttribute(deleteId); // ✅ Must match backend _id
      // toast.success("✅ Variant attribute deleted successfully");
      toast.success(` ${window.translate("Variant attribute deleted successfully")}`);

      const updatedData = await getAllVariantAttributes();
      setDataSource(updatedData);
      setDeleteId(null);
      // Delay then reload
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (err) {
      console.error("Delete failed:", err);
      
      toast.error(`❌ ${window.translate("Failed to delete variant attribute")}`);
    }
  };

  const lang = localStorage.getItem("lang") || "en";

  const columns = [
    {
      title: window.translate("Variant"),
      dataIndex: "variant",
      render: (_, record) =>
        typeof record.name === "object" ? record.name[lang] : record.variant,
      sorter: (a, b) =>
        (a.name?.[lang] || "").localeCompare(b.name?.[lang] || ""),
    },
    {
      title: window.translate("Values"),
      dataIndex: "values",
      render: (_, record) => {
        if (!Array.isArray(record.values)) return "-";
        return record.values
          .map((val) => {
            if (typeof val === "object" && val !== null) {
              return val[lang];
            }
            return String(val); // fallback
          })
          .join(", ");
      },
      sorter: (a, b) => (a.values?.length || 0) - (b.values?.length || 0),
    },

    {
      title: window.translate("Created On"),
      dataIndex: "createdon",
      sorter: (a, b) => a.createdon.length - b.createdon.length,
    },

    {
      title: window.translate("Status"),
      dataIndex: "status",
      render: (text) => {
        const isActive =
          String(text).toLowerCase() === "active" ||
          String(text).toLowerCase() === "on" ||
          String(text) === "true";

        return (
          <span
            className={`badge table-badge fw-medium fs-10 ${
              isActive ? "bg-success" : "bg-danger"
            }`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
      sorter: (a, b) => String(a.status).localeCompare(String(b.status)),
    },

    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) =>
        canWriteVariants ? (
          <div className="action-table-data">
            <div className="edit-delete-action">
              <Link
                className="me-2 p-2"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#edit-units"
                onClick={() => handleEditOpen(record)}
              >
                <i data-feather="edit" className="feather-edit"></i>
              </Link>
              <Link
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#delete-modal"
                className="p-2"
                onClick={() => setDeleteId(record.id)}
              >
                <i data-feather="trash-2" className="feather-trash-2"></i>
              </Link>
            </div>
          </div>
        ) : null,
    },
  ];

  const isColorVariant =
    variantName?.en?.trim()?.toLowerCase()?.includes("color") ||
    false ||
    variantName?.ar?.trim()?.toLowerCase()?.includes("لون") ||
    false;

  const isEditColorVariant =
    editName?.en?.trim()?.toLowerCase()?.includes("color") ||
    false ||
    editName?.ar?.trim()?.toLowerCase()?.includes("لون") ||
    false;

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="fw-bold">
                  {window.translate("Variant Attributes")}
                </h4>
                <h6>{window.translate("Manage your variant attributes")}</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <RefreshIcon />
              <CollapesIcon />
            </ul>
            {canWriteVariants && (
              <div className="page-btn">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add-units"
                >
                  <i className="ti ti-circle-plus me-2 ms-2"></i> {window.translate("addVariant")}
                </Link>
              </div>
            )}
          </div>
          {/* /product list */}
          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    {window.translate("status")}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("active")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        In{window.translate("active")}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                {dataSource.length > 0 ? (
                  <Table columns={columns} dataSource={dataSource} />
                ) : (
                  <div className="text-center py-4">
                    <p>{window.translate("No variant attributes found.")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* /product list */}
        </div>
        <CommonFooter />
      </div>
      <>
        {/* Add Unit */}
        <div className="modal fade" id="add-units">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="page-wrapper-new p-0">
                <div className="content">
                  <div className="modal-header">
                    <div className="page-title">
                      <h4>{window.translate("addVariant")}</h4>
                    </div>
                    <button
                      type="button"
                      id="close-add-variant-btn"
                      className="close bg-danger text-white fs-16"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </div>

                  <div className="modal-body custom-modal-bodys">
                    <form>
                      {/* Variant Name EN/AR */}
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Variant Name")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={variantName.en}
                          onChange={(e) =>
                            setVariantName({
                              ...variantName,
                              en: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Variant Name")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={variantName.ar}
                          onChange={(e) =>
                            setVariantName({
                              ...variantName,
                              ar: e.target.value,
                            })
                          }
                        />
                      </div>

                      {/* Localized Variant Values */}
                      {/* {variantValues.map((val, index) => (
                        <div className="mb-3 row" key={index}>
                          <div className="col-5">
                            <label className="form-label">Value (EN)</label>
                            <input
                              type="text"
                              className="form-control"
                              value={val.en}
                              onChange={(e) => {
                                const newValues = [...variantValues];
                                newValues[index].en = e.target.value;
                                setVariantValues(newValues);
                              }}
                            />
                          </div>
                          <div className="col-5">
                            <label className="form-label">Value (AR)</label>
                            <input
                              type="text"
                              className="form-control"
                              value={val.ar}
                              onChange={(e) => {
                                const newValues = [...variantValues];
                                newValues[index].ar = e.target.value;
                                setVariantValues(newValues);
                              }}
                            />
                          </div>
                          <div className="col-2 d-flex align-items-end">
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() =>
                                setVariantValues(
                                  variantValues.filter((_, i) => i !== index)
                                )
                              }
                            >
                              <i className="ti ti-trash" />
                            </button>
                          </div>
                        </div>
                      ))} */}
                      {variantValues.map((val, index) => (
                        <div className="mb-3 row" key={index}>
                          <div className="col-4">
                            <label className="form-label">{window.translate("Value")} (EN)</label>
                            <input
                              type="text"
                              className="form-control"
                              value={val.en}
                              onChange={(e) => {
                                const newValues = [...variantValues];
                                newValues[index].en = e.target.value;
                                setVariantValues(newValues);
                              }}
                            />
                          </div>
                          <div className="col-4">
                            <label className="form-label">{window.translate("Value")} (AR)</label>
                            <input
                              type="text"
                              className="form-control"
                              value={val.ar}
                              onChange={(e) => {
                                const newValues = [...variantValues];
                                newValues[index].ar = e.target.value;
                                setVariantValues(newValues);
                              }}
                            />
                          </div>

                          {isColorVariant && (
                            <div className="col-2">
                              <label className="form-label">{window.translate("Color")}</label>
                              <input
                                type="color"
                                className="form-control form-control-color"
                                value={val.colorCode || "#000000"}
                                onChange={(e) => {
                                  const newValues = [...variantValues];
                                  newValues[index].colorCode = e.target.value;
                                  setVariantValues(newValues);
                                }}
                              />
                            </div>
                          )}

                          <div className="col-2 d-flex align-items-end">
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() =>
                                setVariantValues(
                                  variantValues.filter((_, i) => i !== index)
                                )
                              }
                            >
                              <i className="ti ti-trash" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Add Value Button */}
                      <div className="mb-3">
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() =>
                            setVariantValues([
                              ...variantValues,
                              { en: "", ar: "" },
                            ])
                          }
                        >
                          + {window.translate("Add Value")}
                        </button>
                      </div>

                      {/* Status Toggle */}
                      <div className="mb-0 mt-4">
                        <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                          <span className="status-label">
                            {window.translate("status")}
                          </span>
                          <input
                            type="checkbox"
                            id="user2"
                            className="check"
                            checked={status}
                            onChange={(e) => setStatus(e.target.checked)}
                          />
                          <label htmlFor="user2" className="checktoggle" />
                        </div>
                      </div>
                    </form>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                      data-bs-dismiss="modal"
                    >
                      {window.translate("cancel")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary fs-13 fw-medium p-2 px-3"
                      onClick={handleAddVariant}
                    >
                      {window.translate("Add Variant")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Add Unit */}

        {/* Edit Variant */}
        <div className="modal fade" id="edit-units">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="page-wrapper-new p-0">
                <div className="content">
                  <div className="modal-header">
                    <div className="page-title">
                      <h4>{window.translate("editVariant")}</h4>
                    </div>
                    <button
                      type="button"
                      className="close bg-danger text-white fs-16"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </div>

                  <div className="modal-body custom-modal-bodys">
                    <form>
                      {/* Variant Name EN/AR */}
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Variant Name")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editName?.en || ""}
                          onChange={(e) =>
                            setEditName({ ...editName, en: e.target.value })
                          }
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Variant Name")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editName?.ar || ""}
                          onChange={(e) =>
                            setEditName({ ...editName, ar: e.target.value })
                          }
                        />
                      </div>

                      {Array.isArray(editValues) &&
                        editValues.map((val, index) => (
                          <div className="mb-3 row" key={index}>
                            {/* EN Field */}
                            <div className="col-4">
                              <label className="form-label">{window.translate("Value")} (EN)</label>
                              <input
                                type="text"
                                className="form-control"
                                value={val.en}
                                onChange={(e) => {
                                  const newValues = [...editValues];
                                  newValues[index].en = e.target.value;
                                  setEditValues(newValues);
                                }}
                              />
                            </div>

                            {/* AR Field */}
                            <div className="col-4">
                              <label className="form-label">{window.translate("Value")} (AR)</label>
                              <input
                                type="text"
                                className="form-control"
                                value={val.ar}
                                onChange={(e) => {
                                  const newValues = [...editValues];
                                  newValues[index].ar = e.target.value;
                                  setEditValues(newValues);
                                }}
                              />
                            </div>

                            {/* Color Picker if it's a color variant */}
                            {isEditColorVariant && (
                              <div className="col-2">
                                <label className="form-label">{window.translate("Color")}</label>
                                <input
                                  type="color"
                                  className="form-control form-control-color"
                                  value={val.colorCode || "#000000"}
                                  onChange={(e) => {
                                    const newValues = [...editValues];
                                    newValues[index].colorCode = e.target.value;
                                    setEditValues(newValues);
                                  }}
                                />
                              </div>
                            )}

                            {/* Delete Button */}
                            <div className="col-2 d-flex align-items-end">
                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() =>
                                  setEditValues(
                                    editValues.filter((_, i) => i !== index)
                                  )
                                }
                              >
                                <i className="ti ti-trash" />
                              </button>
                            </div>
                          </div>
                        ))}

                      {/* Add Value Button */}
                      <div className="mb-3">
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() =>
                            setEditValues([...editValues, { en: "", ar: "", colorCode: "" }])
                          }
                        >
                          + {window.translate("Add Value")}
                        </button>
                      </div>

                      {/* Status Toggle */}
                      <div className="mb-0 mt-4">
                        <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                          <span className="status-label">
                            {window.translate("status")}
                          </span>
                          <input
                            type="checkbox"
                            id="edit-user-status"
                            className="check"
                            checked={editStatus}
                            onChange={(e) => setEditStatus(e.target.checked)}
                          />
                          <label
                            htmlFor="edit-user-status"
                            className="checktoggle"
                          />
                        </div>
                      </div>
                    </form>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                      data-bs-dismiss="modal"
                    >
                      {window.translate("cancel")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary fs-13 fw-medium p-2 px-3"
                      onClick={handleUpdateVariant}
                    >
                      {window.translate("saveChanges")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Edit Variant */}
        <>
          {/* delete modal */}
          <div className="modal fade" id="delete-modal">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="page-wrapper-new p-0">
                  <div className="content p-5 px-3 text-center">
                    <span className="rounded-circle d-inline-flex p-2 bg-danger-transparent mb-2">
                      <i className="ti ti-trash fs-24 text-danger" />
                    </span>
                    <h4 className="fs-20 fw-bold mb-2 mt-1">{window.translate("Delete")}</h4>
                    <p className="mb-0 fs-16">
                      {window.translate("Are you sure you want to delete variant?")}
                    </p>
                    <div className="modal-footer-btn mt-3 d-flex justify-content-center">
                      <button
                        type="button"
                        className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                        data-bs-dismiss="modal"
                      >
                        {window.translate("cancel")}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary fs-13 fw-medium p-2 px-3"
                        data-bs-dismiss="modal"
                        onClick={handleDeleteVariant}
                      >
                        {window.translate("Yes Delete")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      </>
    </>
  );
};

export default VariantAttributes;
